import { Position, Handle } from '@xyflow/react'
import { Tag, Typography, Avatar } from 'antd'
import { memo } from 'react'
import { AiOutlinePlus } from 'react-icons/ai'

import { LinkToResourceWrapper } from '@/Core/Components/Resource'
import { cx } from '@/Ship/Helpers/cssClass'

import { DepartmentResource } from '../../Resources/DepartmentResource'

import styles from './CustomNode.module.css'
import ItemCrudDropdown from './ItemCrudDropdown'

import type { DepartmentNodeData } from '../../Contracts/DepartmentTree.types'

const { Top, Bottom, Left, Right } = Position
const { Text } = Typography

interface CustomNodeProps {
    data: DepartmentNodeData
}

export default memo(({ data }: CustomNodeProps) => {
    const { isSpouse, isSibling, label, direction, crudContext, currentUserId } = data

    const chiefs = data.chiefs ?? []
    const users = data.users ?? []

    const isCurrentUserDepartment =
        chiefs.find((chief) => chief.id === currentUserId) || users.find((user) => user.id === currentUserId)

    const parent = { '@id': data['@id'], title: label }

    const isTreeHorizontal = direction === 'LR'

    const getTargetPosition = () => {
        if (isSpouse) {
            return isTreeHorizontal ? Top : Left
        } else if (isSibling) {
            return isTreeHorizontal ? Bottom : Right
        }
        return isTreeHorizontal ? Left : Top
    }

    const isRootNode = data?.isRoot

    const children = data?.children ?? []
    const hasChildren = children.length > 0

    const firstChief = chiefs[0]
    const chiefPosition = typeof firstChief?.position === 'string' ? firstChief.position : firstChief?.position?.title

    return (
        <div className={cx(styles, 'nodeContainer')}>
            {hasChildren && (
                <Handle
                    className={cx(styles, 'hiddenHandle')}
                    type="source"
                    position={isTreeHorizontal ? Right : Bottom}
                    id={isTreeHorizontal ? Right : Bottom}
                />
            )}
            {!isRootNode && (
                <Handle
                    className={cx(styles, 'hiddenHandle')}
                    type="target"
                    position={getTargetPosition()}
                    id={getTargetPosition()}
                />
            )}

            <div className={cx(styles, 'wrapperContainer')}>
                <div className={cx(styles, 'headerBox')}>
                    <div>
                        <Text className={cx(styles, 'nodeLabel')}>{label}</Text>
                        {isCurrentUserDepartment && <Tag color="blue">ваш відділ</Tag>}
                    </div>
                    <div className={cx(styles, 'nodeButtons')}>
                        {DepartmentResource.accessFn('create') && (
                            <Typography.Link
                                className={cx(styles, 'customButtonLink')}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.preventDefault()
                                    crudContext?.actions?.create?.({
                                        parent,
                                    })
                                }}
                            >
                                <AiOutlinePlus style={{ fontSize: 20 }} />
                            </Typography.Link>
                        )}
                        <ItemCrudDropdown crudContext={crudContext} item={data} />
                    </div>
                </div>
                {chiefs.length === 0 ? (
                    <Text>немає кірівника</Text>
                ) : (
                    <LinkToResourceWrapper
                        {...(firstChief?.['@type'] ? { entityType: firstChief['@type'] } : {})}
                        {...(firstChief?.id !== undefined ? { entityId: firstChief.id } : {})}
                    >
                        <div className={cx(styles, 'userInfoContainer')}>
                            <Avatar
                                className={cx(styles, 'avatar')}
                                src={firstChief?.avatar?.fileUrl ?? '/assets/images/no-profile-picture.png'}
                            />
                            <div className={cx(styles, 'info')}>
                                <Text className={cx(styles, 'name')}>{firstChief?.fullName || '‒'}</Text>
                                <Text className={cx(styles, 'position')}>{chiefPosition || '‒'}</Text>
                            </div>
                        </div>
                    </LinkToResourceWrapper>
                )}
                <Text className={cx(styles, 'employeesCount')}>
                    {users.length > 0 ? (
                        <Text>{`підлеглі: ${users.length}`}</Text>
                    ) : (
                        <Text>немає співробітнитників у підпорядкуванні</Text>
                    )}
                </Text>
            </div>

            <Text className={cx(styles, 'departmentInfo')}>
                {children.length > 0 ? (
                    <Text>{`кількість відділів: ${children.length}`}</Text>
                ) : (
                    <Text className={cx(styles, 'departmentTextNoData')}>немає відділів у підпорядкуванні</Text>
                )}
            </Text>
        </div>
    )
})
